/**
 * ArchetypeBehavior — pure pose math for the mob visual archetype system.
 *
 * Each driver computes a per-frame pose OVERLAY (offsets + multipliers) from
 * the mob's resolved visual config and a time value. The renderer owns one
 * `ArchetypePose` per mob and passes it in — the drivers mutate it in place,
 * so there are **zero per-frame allocations** (Animation principle #2 in
 * `docs/design/coil-art/ANIMATION_REFERENCE.md`).
 *
 * The archetype is the feel (principle #1): a grounder breathes on the
 * ground, a floater drifts and tilts, a lumberer sways under its own weight,
 * a phantom pulses in and out of existence, a skitterer twitches.
 *
 * Reduced-motion (accessibility): amplitudes shrink, strobes flatten to
 * steady dims, and death animations degrade to a plain fade. The drivers
 * take a `reduceMotion` boolean so they stay pure/testable — the renderer
 * reads `getMotionPrefs()` once per frame and passes it down.
 *
 * Lane E — Coil Art Pack (archetype vibe layer). Keyframe source:
 * `docs/design/coil-art/ANIMATION_REFERENCE.md`.
 */

import type { ResolvedMobVisualConfig } from './MobVisualArchetypes';

// ── Pose ──────────────────────────────────────────────────────────────

/** Per-frame visual overlay. The renderer applies these on top of the mob's
 *  base transform (position from sim, scale from art config, alpha 1). */
export interface ArchetypePose {
  /** World-space position offset in px. */
  dx: number;
  dy: number;
  /** Rotation in degrees (floaters tilt as they drift). */
  angleDeg: number;
  /** Multiplied onto the sprite's base alpha (phantom pulse, death fades). */
  alphaMul: number;
  /** Multiplied onto the sprite's base scale (squash/stretch, sink, crouch). */
  scaleXMul: number;
  scaleYMul: number;
  /** True while the hit/telegraph flash tint should be applied this frame. */
  flash: boolean;
}

export function createPose(): ArchetypePose {
  return { dx: 0, dy: 0, angleDeg: 0, alphaMul: 1, scaleXMul: 1, scaleYMul: 1, flash: false };
}

export function resetPose(pose: ArchetypePose): void {
  pose.dx = 0;
  pose.dy = 0;
  pose.angleDeg = 0;
  pose.alphaMul = 1;
  pose.scaleXMul = 1;
  pose.scaleYMul = 1;
  pose.flash = false;
}

// ── Math helpers (no allocations) ─────────────────────────────────────

const TWO_PI = Math.PI * 2;

function sinCycle(tMs: number, periodMs: number): number {
  return Math.sin((TWO_PI * tMs) / periodMs);
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function easeOutCubic(k: number): number {
  const inv = 1 - k;
  return 1 - inv * inv * inv;
}

/** A 0→1→0 bell over k∈[0,1], peaking at `peak`. Used for squash impulses. */
function bell(k: number, peak: number): number {
  if (k <= 0 || k >= 1) return 0;
  return k < peak ? k / peak : (1 - k) / (1 - peak);
}

// ── Idle ──────────────────────────────────────────────────────────────

/**
 * Idle pose — the always-on "alive" motion, shaped by archetype:
 * - grounder: breathes — slow bob + tiny offset sway + bottom squash
 * - floater:  drifts — bigger bob + horizontal drift + ±2° tilt
 * - skitterer: twitches — fast bounce + horizontal jitter
 * - lumberer: sways — slow horizontal sway + weight compression, no bounce
 * - phantom:  pulses — alpha 0.7↔1.0 + synced 3% scale pulse, no bob
 *
 * `moving` gives grounded archetypes a procedural move-bounce (faster,
 * slightly bigger) so walking reads without a separate hop tween.
 */
export function computeIdlePose(
  cfg: ResolvedMobVisualConfig,
  tMs: number,
  pose: ArchetypePose,
  reduceMotion = false,
  moving = false,
): void {
  resetPose(pose);
  const { bobAmpPx, bobPeriodMs, sway } = cfg.idle;
  const damp = reduceMotion ? 0.25 : 1;

  switch (cfg.archetype) {
  case 'grounder': {
    const period = moving ? bobPeriodMs * 0.5 : bobPeriodMs;
    const amp = (moving ? bobAmpPx * 1.5 : bobAmpPx) * damp;
    // Bounce (|sin|) while moving, breathe (sin) at rest.
    const wave = moving ? Math.abs(sinCycle(tMs, period)) : sinCycle(tMs, period);
    pose.dy = -wave * amp;
    // Sway offset from the bob (reference: bob 2s, sway 3s → ×1.6).
    pose.dx = sway * damp * sinCycle(tMs, period * 1.6);
    // Body squashes slightly at the bottom of the bob (0.98 → 1.02).
    pose.scaleYMul = 1 + 0.02 * wave * damp;
    break;
  }
  case 'floater': {
    const period = moving ? bobPeriodMs * 0.75 : bobPeriodMs;
    pose.dy = -sinCycle(tMs, period) * bobAmpPx * damp;
    pose.dx = sway * damp * sinCycle(tMs, period * 1.67);
    // Tilts as it floats (±2°).
    pose.angleDeg = 2 * damp * sinCycle(tMs, period * 1.33);
    break;
  }
  case 'skitterer': {
    const amp = (moving ? bobAmpPx * 1.5 : bobAmpPx) * damp;
    pose.dy = -Math.abs(sinCycle(tMs, bobPeriodMs)) * amp;
    // Deterministic pseudo-jitter (two incommensurate sines) — reads
    // twitchy without Math.random. Disabled under reduced motion.
    if (!reduceMotion) {
      pose.dx = Math.sin(tMs * 0.093) * Math.cos(tMs * 0.047) * (sway || 1);
    }
    break;
  }
  case 'lumberer': {
    const period = moving ? bobPeriodMs * 0.7 : bobPeriodMs;
    // Slow horizontal sway — the mob is heavy, barely moving.
    pose.dx = (sway || 2) * damp * sinCycle(tMs, period);
    // Compresses under its own weight (1.0 → 0.98 → 1.0).
    pose.scaleYMul = 1 - 0.02 * damp * (0.5 + 0.5 * sinCycle(tMs, period));
    break;
  }
  case 'phantom': {
    // Ethereal pulse — no bob; opacity + scale breathe together.
    const wave = 0.5 + 0.5 * sinCycle(tMs, bobPeriodMs * 2.5); // ~2s at default 800ms
    if (reduceMotion) {
      pose.alphaMul = 0.9; // steady, no pulse
    } else {
      pose.alphaMul = 0.7 + 0.3 * wave;
      pose.scaleXMul = pose.scaleYMul = 1 + 0.03 * wave;
    }
    break;
  }
  }
}

// ── Hit-react ─────────────────────────────────────────────────────────

/** How long each archetype's hit-react lasts (recoil + recover). */
export function hitReactDurationMs(cfg: ResolvedMobVisualConfig): number {
  switch (cfg.archetype) {
  case 'skitterer': return 150;
  case 'lumberer': return 600;  // 200ms squash + slow 400ms ease back
  case 'phantom': return 200;
  case 'floater': return 350;   // recoil + spin wobble settle
  default: return 250;          // grounder: 150ms recoil + squash recover
  }
}

/**
 * Hit-react overlay. `dirX` is the recoil direction (+1 right / -1 left).
 * Returns true while active; the renderer stops calling once false.
 *
 * Two modes (config `hitReact.mode`):
 * - 'flash' — recoil + squash + a flash tint on the first ~80ms
 * - 'phase' — spectral: alpha dips to 0.3 and back, small blink offset,
 *   NO flash (phantoms phase through the hit)
 */
export function computeHitReactPose(
  cfg: ResolvedMobVisualConfig,
  elapsedMs: number,
  dirX: number,
  pose: ArchetypePose,
  reduceMotion = false,
): boolean {
  resetPose(pose);
  const durMs = hitReactDurationMs(cfg);
  if (elapsedMs < 0 || elapsedMs >= durMs) return false;
  const k = clamp01(elapsedMs / durMs);

  if (cfg.hitReact.mode === 'phase') {
    // Alpha dip 1 → 0.3 → 1 + a small sideways blink.
    pose.alphaMul = 1 - 0.7 * bell(k, 0.4);
    if (!reduceMotion) pose.dx = dirX * 3 * bell(k, 0.3);
    return true;
  }

  // Flash on the leading edge (~80ms). Photosensitivity: suppressed under
  // reduced motion — the recoil still reads the hit.
  pose.flash = !reduceMotion && elapsedMs < 80;
  const damp = reduceMotion ? 0.4 : 1;

  // Recoil: strongest at impact, eases out.
  pose.dx = dirX * cfg.hitReact.recoilPx * (1 - easeOutCubic(k)) * damp;

  // Squash impulse. Lumberers hold the squash longer (heavy).
  const squashPeak = cfg.archetype === 'lumberer' ? 0.33 : 0.25;
  const squash = cfg.hitReact.squash * bell(k, squashPeak) * damp;
  pose.scaleYMul = 1 - squash;
  pose.scaleXMul = 1 + squash * 0.6;

  // Floater: spin wobble (±10° damped).
  if (cfg.archetype === 'floater' && !reduceMotion) {
    pose.angleDeg = 10 * (1 - k) * Math.sin(TWO_PI * k * 2);
  }
  return true;
}

// ── Death ─────────────────────────────────────────────────────────────

/**
 * Death animation overlay. Returns true while animating; once it returns
 * false the renderer holds the final pose (corpse) until despawn/respawn.
 *
 * Modes (ANIMATION_REFERENCE.md):
 * - sink:     shrinks vertically + sinks 20px into the ground, fades late
 * - dissolve: fades while drifting 30px upward
 * - shatter:  brief violent shake, then collapses + fades (fragment chunks
 *             are approximated by a renderer-side particle burst)
 * - poof:     near-instant vanish
 *
 * Reduced motion: every mode degrades to a plain fade (no movement/shake).
 */
export function computeDeathPose(
  cfg: ResolvedMobVisualConfig,
  elapsedMs: number,
  pose: ArchetypePose,
  reduceMotion = false,
): boolean {
  resetPose(pose);
  const durMs = cfg.death.durMs;
  const k = clamp01(elapsedMs / durMs);

  if (reduceMotion) {
    pose.alphaMul = 1 - k;
    return k < 1;
  }

  switch (cfg.death.mode) {
  case 'sink': {
    pose.scaleYMul = 1 - k;
    pose.dy = 20 * k;
    // Fade over the last third.
    pose.alphaMul = k > 2 / 3 ? 1 - (k - 2 / 3) * 3 : 1;
    break;
  }
  case 'dissolve': {
    pose.alphaMul = 1 - k;
    pose.dy = -30 * k;
    break;
  }
  case 'shatter': {
    if (k < 0.3) {
      // The cracks spread — violent shake + pre-collapse squash.
      const kk = k / 0.3;
      pose.dx = 2 * Math.sin(TWO_PI * kk * 5);
      pose.scaleYMul = 1 - 0.2 * kk;
    } else {
      // Collapse + fade — the fragments (particles) carry the read.
      const kk = (k - 0.3) / 0.7;
      pose.scaleYMul = 0.8 - 0.6 * kk;
      pose.scaleXMul = 1 + 0.15 * kk;
      pose.alphaMul = 1 - kk;
      pose.dy = 10 * kk;
    }
    break;
  }
  case 'poof': {
    // Instant vanish — gone in the first 50ms regardless of durMs.
    pose.alphaMul = 1 - clamp01(elapsedMs / 50);
    pose.scaleXMul = pose.scaleYMul = 1 + 0.3 * clamp01(elapsedMs / 50);
    break;
  }
  }
  return k < 1;
}

// ── Telegraph ─────────────────────────────────────────────────────────

/**
 * Pre-attack wind-up self-tell. Returns true while active.
 *
 * Cues (config `telegraph.cue`):
 * - crouch:  squats down + widens over tellMs (grounder/lumberer)
 * - grow:    scales up 15% with a pulse at the end (floater)
 * - flash:   strobing flash tint (skitterer) — steady dim under reduced motion
 * - flicker: rapid alpha oscillation (phantom) — steady under reduced motion
 * - slam:    rises and leans back for 75% of the tell, then drives down into a
 *            wide squash (stone golem) — a steady stretch under reduced motion
 */
export function computeTelegraphPose(
  cfg: ResolvedMobVisualConfig,
  elapsedMs: number,
  pose: ArchetypePose,
  reduceMotion = false,
  durationMs = cfg.telegraph.tellMs,
): boolean {
  resetPose(pose);
  const tellMs = durationMs;
  if (!Number.isFinite(tellMs) || tellMs <= 0) return false;
  if (elapsedMs < 0 || elapsedMs >= tellMs) return false;
  const k = clamp01(elapsedMs / tellMs);

  switch (cfg.telegraph.cue) {
  case 'crouch': {
    const e = easeOutCubic(k);
    pose.scaleYMul = 1 - 0.15 * e;
    pose.scaleXMul = 1 + 0.12 * e;
    break;
  }
  case 'grow': {
    pose.scaleXMul = pose.scaleYMul = 1 + 0.15 * easeOutCubic(k)
        + (k > 0.85 && !reduceMotion ? 0.05 * Math.sin(TWO_PI * (k - 0.85) / 0.15) : 0);
    break;
  }
  case 'flash': {
    // 8Hz strobe; photosensitivity-safe fallback is a steady dim.
    pose.flash = reduceMotion ? false : Math.sin(TWO_PI * elapsedMs / 125) > 0;
    if (reduceMotion) pose.alphaMul = 0.8;
    break;
  }
  case 'flicker': {
    pose.alphaMul = reduceMotion ? 0.8 : 0.75 + 0.25 * Math.sin(TWO_PI * elapsedMs / 100);
    break;
  }
  case 'slam': {
    if (reduceMotion) {
      pose.scaleYMul = 1.06;
      break;
    }
    if (k < 0.75) {
      const e = easeOutCubic(k / 0.75);
      pose.scaleYMul = 1 + 0.12 * e;
      pose.scaleXMul = 1 - 0.05 * e;
      pose.dy = -4 * e;
      pose.angleDeg = -4 * e;
    } else {
      const e = easeOutCubic((k - 0.75) / 0.25);
      pose.scaleYMul = 1.12 - 0.27 * e;
      pose.scaleXMul = 0.95 + 0.2 * e;
      pose.dy = -4 + 6 * e;
      pose.angleDeg = -4 + 7 * e;
    }
    break;
  }
  }
  return true;
}

// ── Vibe particles ────────────────────────────────────────────────────

/** A vibe particle emission recipe — which existing EnhancedParticleManager
 *  emitter approximates the designed particle kind, how many, how often,
 *  and where relative to the mob (feet vs center). */
export interface VibeParticleRecipe {
  emitterId: string;
  count: number;
  periodMs: number;
  /** 'feet' = at the base of the sprite; 'center' = around the body. */
  at: 'feet' | 'center';
  /** Only emit while the mob is moving (lumberer footfall dust, trails). */
  whenMoving: boolean;
}

/**
 * Map a config `vibe.particleKind` to an emission recipe using the particle
 * emitters that exist today ('landing_dust' / 'sparkle' / 'smoke').
 * Dedicated tinted mob-vibe emitters are a follow-up — the aura glow sprite
 * carries the color signature meanwhile.
 */
const VIBE_RECIPES: Record<string, VibeParticleRecipe> = {
  dust: { emitterId: 'landing_dust', count: 2, periodMs: 2000, at: 'feet', whenMoving: false },
  heavy_dust: { emitterId: 'landing_dust', count: 3, periodMs: 1200, at: 'feet', whenMoving: true },
  sparkle: { emitterId: 'sparkle', count: 1, periodMs: 1800, at: 'center', whenMoving: false },
  pollen: { emitterId: 'sparkle', count: 1, periodMs: 1500, at: 'center', whenMoving: true },
  spore: { emitterId: 'smoke', count: 1, periodMs: 2500, at: 'center', whenMoving: false },
  wisp: { emitterId: 'smoke', count: 1, periodMs: 1600, at: 'center', whenMoving: false },
  root_chips: { emitterId: 'smoke', count: 1, periodMs: 2200, at: 'feet', whenMoving: true },
  corruption_flecks: { emitterId: 'sparkle', count: 2, periodMs: 1400, at: 'center', whenMoving: false },
  eldritch_glow: { emitterId: 'sparkle', count: 1, periodMs: 2000, at: 'center', whenMoving: false },
  // Stone golem: grit sifting off the plates even when it stands still.
  rubble_dust: { emitterId: 'landing_dust', count: 2, periodMs: 1500, at: 'feet', whenMoving: false },
};

export function getVibeParticleRecipe(particleKind: string | undefined): VibeParticleRecipe | null {
  if (!particleKind) return null;
  return VIBE_RECIPES[particleKind] ?? null;
}
